import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import LocationSearch from '@/components/LocationSearch';
import apiClient from '@/utils/api';
import { resolveInitialCity, setStoredCity } from '@/utils/geo';
import { 
  Home, Building2, Briefcase, Search, MapPin, Bed, Bath, 
  Square, Heart, Phone, ArrowRight, Filter, Wifi, Car,
  Utensils, Dumbbell, Shield, Star, Loader2, X, SlidersHorizontal,
  Sparkles, PawPrint, Train, Sofa, TreePine, Zap, ChevronDown,
  Grid3X3, LayoutList, IndianRupee
} from 'lucide-react';

const propertyTypes = [
  { value: 'all', label: 'All Types', icon: Home },
  { value: 'residential', label: 'Residential', icon: Home },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
  { value: 'studio', label: 'Studio', icon: Briefcase },
];

// Enhanced quick selection filters with categories
const quickFilterCategories = [
  {
    category: 'Budget',
    icon: IndianRupee,
    filters: [
      { label: 'Under ₹15K', filter: { maxRent: '15000' }, color: 'emerald' },
      { label: '₹15K - ₹30K', filter: { minRent: '15000', maxRent: '30000' }, color: 'emerald' },
      { label: '₹30K - ₹50K', filter: { minRent: '30000', maxRent: '50000' }, color: 'emerald' },
      { label: '₹50K+', filter: { minRent: '50000' }, color: 'emerald' },
    ]
  },
  {
    category: 'Configuration',
    icon: Bed,
    filters: [
      { label: '1 BHK', filter: { bedrooms: '1' }, color: 'blue' },
      { label: '2 BHK', filter: { bedrooms: '2' }, color: 'blue' },
      { label: '3 BHK', filter: { bedrooms: '3' }, color: 'blue' },
      { label: '4+ BHK', filter: { bedrooms: '4' }, color: 'blue' },
    ]
  },
  {
    category: 'Features',
    icon: Sparkles,
    filters: [
      { label: 'Furnished', filter: { furnished: 'yes' }, icon: Sofa, color: 'amber' },
      { label: 'Semi-Furnished', filter: { furnished: 'semi' }, icon: Sofa, color: 'amber' },
      { label: 'Pet Friendly', filter: { petFriendly: 'yes' }, icon: PawPrint, color: 'pink' },
      { label: 'Near Metro', filter: { nearMetro: 'yes' }, icon: Train, color: 'violet' },
    ]
  },
  {
    category: 'Amenities',
    icon: Zap,
    filters: [
      { label: 'Parking', filter: { parking: 'yes' }, icon: Car, color: 'slate' },
      { label: 'Gym', filter: { gym: 'yes' }, icon: Dumbbell, color: 'orange' },
      { label: 'Garden', filter: { garden: 'yes' }, icon: TreePine, color: 'green' },
      { label: 'Power Backup', filter: { powerBackup: 'yes' }, icon: Zap, color: 'yellow' },
    ]
  }
];

// Flatten for backward compatibility
const quickFilters = quickFilterCategories.flatMap(cat => cat.filters);

const RENTALS_CACHE_KEY = 'zenstudios_rent_search';

const filterSignature = (f) => JSON.stringify([f.location, f.type, f.minRent, f.maxRent, f.bedrooms, f.furnished]);

const RentalCard = ({ listing, formatCurrency, viewMode = 'grid' }) => (
  <Link
    to={`/rentals/${listing.rental_id || listing.id}`}
    className={`bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-primary/30 hover:shadow-xl transition-all duration-300 group block ${
      viewMode === 'list' ? 'flex' : ''
    }`}
    data-testid={`rental-card-${listing.rental_id || listing.id}`}
  >
    {/* Image */}
    <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-72 flex-shrink-0' : ''}`}>
      <img
        src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'}
        alt={listing.title}
        loading="lazy"
        decoding="async"
        className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${
          viewMode === 'list' ? 'h-full' : 'h-52'
        }`}
      />
      {listing.featured && (
        <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
          <Star className="w-3 h-3" /> Featured
        </div>
      )}
      <button
        className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-md"
        onClick={(e) => e.preventDefault()}
      >
        <Heart className="w-5 h-5 text-slate-600 hover:text-red-500" />
      </button>
      <div className="absolute bottom-4 left-4">
        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
          listing.property_type?.toLowerCase() === 'residential' ? 'bg-primary text-white' :
          listing.property_type?.toLowerCase() === 'commercial' ? 'bg-blue-600 text-white' :
          'bg-violet-600 text-white'
        }`}>
          {listing.property_type || 'Residential'}
        </span>
      </div>
    </div>

    {/* Content */}
    <div className="p-5 flex-1">
      <h3 className="font-semibold text-lg text-slate-900 mb-2 group-hover:text-primary transition-colors line-clamp-1">
        {listing.title}
      </h3>
      <div className="flex items-center text-slate-500 text-sm mb-4">
        <MapPin className="w-4 h-4 mr-1 text-primary" />
        {listing.locality ? `${listing.locality}, ${listing.city}` : listing.city || 'Location'}
      </div>

      {/* Features */}
      <div className="flex gap-4 text-sm text-slate-600 mb-4">
        {listing.bedrooms && (
          <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-lg">
            <Bed className="w-4 h-4 text-slate-400" />
            <span className="font-medium">{listing.bedrooms}</span> Bed
          </div>
        )}
        {listing.bathrooms && (
          <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-lg">
            <Bath className="w-4 h-4 text-slate-400" />
            <span className="font-medium">{listing.bathrooms}</span> Bath
          </div>
        )}
        {listing.area_sqft && (
          <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-lg">
            <Square className="w-4 h-4 text-slate-400" />
            <span className="font-medium">{listing.area_sqft}</span> sqft
          </div>
        )}
      </div>

      {/* Amenities */}
      {listing.amenities && listing.amenities.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {listing.amenities.slice(0, 3).map((amenity, idx) => (
            <span key={idx} className="text-xs bg-primary/5 text-primary px-2.5 py-1 rounded-full font-medium">
              {amenity}
            </span>
          ))}
          {listing.amenities.length > 3 && (
            <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
              +{listing.amenities.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Price & CTA */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <div>
          <p className="text-2xl font-bold text-primary">
            {formatCurrency(listing.rent)}
            <span className="text-sm font-normal text-slate-500">/mo</span>
          </p>
          {listing.deposit && (
            <p className="text-xs text-slate-500">Deposit: {formatCurrency(listing.deposit)}</p>
          )}
        </div>
        <Button
          size="sm"
          className="rounded-xl bg-primary hover:bg-primary/90 shadow-sm"
          onClick={(e) => {
            e.preventDefault();
            window.open(`https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20interested%20in%20${encodeURIComponent(listing.title)}%20at%20${encodeURIComponent(listing.locality || listing.city)}`, '_blank');
          }}
        >
          Enquire <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  </Link>
);

const FindRentals = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    type: 'all',
    minRent: '',
    maxRent: '',
    bedrooms: 'any',
    location: searchParams.get('city') || '',
    furnished: '',
    petFriendly: '',
    nearMetro: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeQuickFilters, setActiveQuickFilters] = useState([]);
  const [rentalListings, setRentalListings] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [fallbackLevel, setFallbackLevel] = useState(null);
  const [searchedCity, setSearchedCity] = useState('');
  const [searchedState, setSearchedState] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const initRef = useRef(false);
  const lastSigRef = useRef(null);

  const persistSearch = (data, usedFilters) => {
    try {
      sessionStorage.setItem(RENTALS_CACHE_KEY, JSON.stringify({
        filters: usedFilters,
        rentals: data.rentals || [],
        fallback: data.fallback || [],
        fallbackLevel: data.fallback_level || null,
        searchedCity: data.searched_city || usedFilters.location || '',
        searchedState: data.searched_state || '',
      }));
    } catch {
      /* ignore storage errors */
    }
  };

  const fetchRentals = async (overrideFilters) => {
    const activeFilters = overrideFilters || filters;
    lastSigRef.current = filterSignature(activeFilters);
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeFilters.location) params.append('city', activeFilters.location);
      if (activeFilters.type && activeFilters.type !== 'all') params.append('property_type', activeFilters.type);
      if (activeFilters.minRent) params.append('min_rent', activeFilters.minRent);
      if (activeFilters.maxRent) params.append('max_rent', activeFilters.maxRent);
      if (activeFilters.bedrooms && activeFilters.bedrooms !== 'any') params.append('bedrooms', activeFilters.bedrooms);
      if (activeFilters.furnished === 'yes') params.append('furnishing', 'furnished');

      const response = await apiClient.get(`/rentals?${params.toString()}`);
      const data = response.data || {};
      setRentalListings(data.rentals || []);
      setFallback(data.fallback || []);
      setFallbackLevel(data.fallback_level || null);
      setSearchedCity(data.searched_city || activeFilters.location || '');
      setSearchedState(data.searched_state || '');
      persistSearch(data, activeFilters);
    } catch (err) {
      console.error('Error fetching rentals:', err);
      setError('Failed to load rentals');
      setRentalListings([]);
      setFallback([]);
      setFallbackLevel(null);
    } finally {
      setLoading(false);
    }
  };

  // On mount: restore last search (back-navigation) or run a fresh
  // location-aware search using hybrid IP detection / stored city.
  useEffect(() => {
    const init = async () => {
      try {
        const cached = sessionStorage.getItem(RENTALS_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          const restored = parsed.filters || filters;
          setFilters((prev) => ({ ...prev, ...parsed.filters }));
          setRentalListings(parsed.rentals || []);
          setFallback(parsed.fallback || []);
          setFallbackLevel(parsed.fallbackLevel || null);
          setSearchedCity(parsed.searchedCity || '');
          setSearchedState(parsed.searchedState || '');
          lastSigRef.current = filterSignature(restored);
          setLoading(false);
          initRef.current = true;
          return;
        }
      } catch {
        /* ignore parse errors */
      }

      const urlCity = searchParams.get('city') || '';
      const city = await resolveInitialCity(urlCity);
      const initFilters = { ...filters, location: city };
      setFilters(initFilters);
      await fetchRentals(initFilters);
      initRef.current = true;
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced re-fetch when filters change (after initial load)
  useEffect(() => {
    if (!initRef.current) return;
    if (filterSignature(filters) === lastSigRef.current) return;
    const debounceTimer = setTimeout(() => {
      if (filters.location) setStoredCity(filters.location);
      fetchRentals();
    }, 500);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location, filters.type, filters.minRent, filters.maxRent, filters.bedrooms, filters.furnished]);

  const handleQuickFilter = (quickFilter, label) => {
    if (activeQuickFilters.includes(label)) {
      setActiveQuickFilters(activeQuickFilters.filter(f => f !== label));
      const newFilters = { ...filters };
      Object.keys(quickFilter.filter).forEach(key => {
        if (key === 'bedrooms') newFilters[key] = 'any';
        else newFilters[key] = '';
      });
      setFilters(newFilters);
    } else {
      setActiveQuickFilters([...activeQuickFilters, label]);
      setFilters({ ...filters, ...quickFilter.filter });
    }
  };

  const clearAllFilters = () => {
    setFilters({ type: 'all', minRent: '', maxRent: '', bedrooms: 'any', location: '', furnished: '', petFriendly: '', nearMetro: '' });
    setActiveQuickFilters([]);
    setPriceRange([0, 100000]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredListings = rentalListings.filter(listing => {
    if (filters.type !== 'all') {
      const type = listing.property_type?.toLowerCase() || '';
      if (filters.type === 'studio') {
        if (type !== 'studio' && listing.property_subtype?.toLowerCase() !== 'studio') return false;
      } else if (!type.includes(filters.type)) {
        return false;
      }
    }
    return true;
  });

  const getFilterColor = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
      blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
      pink: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
      violet: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
      slate: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
      orange: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
      green: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    };
    return colors[color] || colors.emerald;
  };

  const getActiveFilterColor = (color) => {
    const colors = {
      emerald: 'bg-emerald-600 text-white border-emerald-600',
      blue: 'bg-blue-600 text-white border-blue-600',
      amber: 'bg-amber-600 text-white border-amber-600',
      pink: 'bg-pink-600 text-white border-pink-600',
      violet: 'bg-violet-600 text-white border-violet-600',
      slate: 'bg-slate-600 text-white border-slate-600',
      orange: 'bg-orange-600 text-white border-orange-600',
      green: 'bg-green-600 text-white border-green-600',
      yellow: 'bg-yellow-600 text-white border-yellow-600',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20" data-testid="find-rentals-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxNTgwM0QiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLTRIMjR2MmgxMnYtMnptLTIgOEgyNnYyaDh2LTJ6bTAtNHYyaDh2LTJoLTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-5 py-2.5 rounded-full text-sm font-semibold mb-6 shadow-sm">
              <Home className="w-4 h-4" />
              Find Your Perfect Rental
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Discover Rental Homes
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Browse through curated residential, commercial, and studio spaces. 
              Find places with minimum essentials to move in right away.
            </p>
          </div>

          {/* Main Search Card */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 p-6 md:p-8">
              {/* Search Bar */}
              <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 mb-6">
                <div className="flex-1">
                  <LocationSearch
                    placeholder="Search by city, locality..."
                    value={filters.location}
                    onChange={(val) => setFilters({...filters, location: val})}
                    types="city,town,locality"
                    className=""
                    inputClassName="h-14 rounded-2xl border-slate-200 text-lg shadow-sm"
                    data-testid="rental-search-input"
                  />
                </div>
                <Select value={filters.type} onValueChange={(v) => setFilters({...filters, type: v})}>
                  <SelectTrigger className="w-full lg:w-56 rounded-2xl border-slate-200 h-14 shadow-sm" data-testid="property-type-select">
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  size="lg"
                  className="rounded-2xl bg-primary hover:bg-primary/90 px-8 h-14 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                  onClick={() => fetchRentals()}
                  data-testid="search-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>

              {/* Quick Filters - Categorized */}
              <div className="space-y-4" data-testid="quick-filters">
                {quickFilterCategories.map((category, catIdx) => (
                  <div key={catIdx} className="space-y-2">
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === catIdx ? null : catIdx)}
                      className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                      data-testid={`filter-category-${category.category.toLowerCase()}`}
                    >
                      <category.icon className="w-4 h-4" />
                      {category.category}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedCategory === catIdx ? 'rotate-180' : ''}`} />
                      {category.filters.some(f => activeQuickFilters.includes(f.label)) && (
                        <span className="ml-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                    </button>
                    <div className={`flex flex-wrap gap-2 transition-all duration-300 ${
                      expandedCategory === catIdx || expandedCategory === null ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'
                    }`}>
                      {category.filters.map((qf, idx) => {
                        const isActive = activeQuickFilters.includes(qf.label);
                        const FilterIcon = qf.icon;
                        return (
                          <button
                            key={idx}
                            onClick={() => handleQuickFilter(qf, qf.label)}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all duration-200 ${
                              isActive 
                                ? getActiveFilterColor(qf.color) + ' shadow-md scale-105'
                                : getFilterColor(qf.color)
                            }`}
                            data-testid={`quick-filter-${qf.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                          >
                            {FilterIcon && <FilterIcon className="w-4 h-4" />}
                            {qf.label}
                            {isActive && <X className="w-3 h-3 ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Active Filters Summary */}
              {activeQuickFilters.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Filter className="w-4 h-4" />
                    <span>{activeQuickFilters.length} filter{activeQuickFilters.length > 1 ? 's' : ''} applied</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-slate-600 hover:text-red-600 rounded-xl"
                    data-testid="clear-all-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}

              {/* Advanced Filters Toggle */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  data-testid="toggle-advanced-filters"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {showFilters ? 'Hide' : 'Show'} Advanced Filters
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>

                {/* Advanced Filters Panel */}
                {showFilters && (
                  <div className="mt-6 p-6 bg-slate-50 rounded-2xl animate-in slide-in-from-top duration-300">
                    <div className="grid md:grid-cols-4 gap-6">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Min Rent</label>
                        <Input
                          type="number"
                          placeholder="₹ Min"
                          className="rounded-xl border-slate-200"
                          value={filters.minRent}
                          onChange={(e) => setFilters({...filters, minRent: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Max Rent</label>
                        <Input
                          type="number"
                          placeholder="₹ Max"
                          className="rounded-xl border-slate-200"
                          value={filters.maxRent}
                          onChange={(e) => setFilters({...filters, maxRent: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">Bedrooms</label>
                        <Select value={filters.bedrooms} onValueChange={(v) => setFilters({...filters, bedrooms: v})}>
                          <SelectTrigger className="rounded-xl border-slate-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="1">1 BHK</SelectItem>
                            <SelectItem value="2">2 BHK</SelectItem>
                            <SelectItem value="3">3 BHK</SelectItem>
                            <SelectItem value="4">4+ BHK</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button 
                          variant="outline" 
                          className="w-full rounded-xl border-slate-200 hover:bg-slate-100"
                          onClick={clearAllFilters}
                        >
                          Reset Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Available Rentals</h2>
              <p className="text-slate-600 mt-1">{filteredListings.length} properties found</p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="view-grid-btn"
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="view-list-btn"
                >
                  <LayoutList className="w-5 h-5" />
                </button>
              </div>
              <Button 
                variant="outline" 
                className="rounded-xl border-slate-200"
                onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20looking%20for%20rental%20properties.', '_blank')}
              >
                <Phone className="w-4 h-4 mr-2" />
                Get Help
              </Button>
            </div>
          </div>

          {/* Listings */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <p className="text-slate-600">Finding the best rentals for you...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mb-4">
                <Search className="w-10 h-10 text-red-300" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Unable to load rentals</h3>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => fetchRentals()} className="rounded-xl">
                Try Again
              </Button>
            </div>
          ) : filteredListings.length === 0 ? (
            <div data-testid="rentals-empty-state">
              {/* No exact matches — friendly verbiage + WhatsApp / check back */}
              <div className="text-center py-12 bg-gradient-to-br from-primary/5 to-white border border-primary/10 rounded-3xl px-6" data-testid="no-rentals-msg">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
                  <Home className="w-10 h-10 text-primary/50" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                  {searchedCity ? `No rentals in ${searchedCity} just yet` : 'No rentals available just yet'}
                </h3>
                <p className="text-slate-600 max-w-xl mx-auto mb-6">
                  We&apos;re constantly adding new rental listings{searchedCity ? ` in ${searchedCity}` : ''}.
                  Please check back again soon — or send us your requirement on WhatsApp and our
                  team will reach out with matching options as they become available.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button
                    className="rounded-full bg-primary px-8"
                    onClick={() => window.open(`https://wa.me/918790460259?text=${encodeURIComponent(`Hi ZenStudios! I'm looking for a rental${searchedCity ? ' in ' + searchedCity : ''}. Please help me find options.`)}`, '_blank')}
                    data-testid="rentals-whatsapp-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" /> Message us on WhatsApp
                  </Button>
                  <Button variant="outline" className="rounded-full px-8" onClick={() => fetchRentals()} data-testid="rentals-check-again-btn">
                    Check Again
                  </Button>
                </div>
              </div>

              {/* Recently-added fallback, widening scope city -> state -> country */}
              {fallback.length > 0 && (
                <div className="mt-12" data-testid="rentals-fallback-section">
                  <h3 className="text-xl font-semibold text-slate-900 mb-1" data-testid="rentals-fallback-heading">
                    {fallbackLevel === 'city' && `Recently added in ${searchedCity || 'your city'}`}
                    {fallbackLevel === 'state' && `Recently added across ${searchedState || 'the state'}`}
                    {fallbackLevel === 'country' && 'Recently added across India'}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">
                    While we find an exact match, here are the latest rentals you might like.
                  </p>
                  <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-testid="rentals-fallback-grid">
                    {fallback.map((listing) => (
                      <RentalCard key={listing.rental_id || listing.id} listing={listing} formatCurrency={formatCurrency} viewMode={viewMode} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-testid="rentals-grid">
              {filteredListings.map((listing) => (
                <RentalCard key={listing.rental_id || listing.id} listing={listing} formatCurrency={formatCurrency} viewMode={viewMode} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary/90 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzR2LTJIMjR2MmgxMnptMC00SDI0djJoMTJ2LTJ6bS0yIDhIMjZ2Mmg4di0yem0wLTR2Mmg4di0yaC04eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Can&apos;t Find What You&apos;re Looking For?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Tell us your requirements and we&apos;ll find the perfect rental property for you
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/buy/post-requirement">
              <Button size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 px-8 shadow-xl">
                Post Your Requirement <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full border-white/50 text-white hover:bg-white/10 px-8"
              onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20help%20finding%20a%20rental%20property.', '_blank')}
            >
              <Phone className="w-5 h-5 mr-2" />
              Talk to Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FindRentals;
